import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Palette, Layers, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import PatternStudioWidget from "@/components/site/PatternStudio";
import ConsultScheduler from "@/components/site/ConsultScheduler";
import { COMPANY, STAMP_COLORS, STAMP_PATTERNS, STAMP_COLOR_FAMILIES } from "@/lib/constants";

const highlights = [
  { icon: Layers, title: "6 Signature Patterns", text: "From Ashlar Slate to Herringbone Brick — the look of natural stone, brick, and timber." },
  { icon: Palette, title: "MasterColor® Palette", text: `The full ${STAMP_COLORS.length}-shade MasterColor lineup CG Concrete pours — grays, neutrals, reds, and earth tones.` },
  { icon: Sparkles, title: "Sealed to Last", text: "UV-resistant sealer keeps color vivid and adds slip resistance underfoot." },
];

export default function PatternStudioPage() {
  const [studioColor, setStudioColor] = useState(STAMP_COLORS[0]);
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Stamped Concrete",
    provider: { "@type": "GeneralContractor", name: COMPANY.name, telephone: COMPANY.phone },
    areaServed: `${COMPANY.city}, ${COMPANY.state}`,
    description: "Stamped concrete pattern and color studio featuring Ashlar Slate, Grand Ashlar, Random Stone, European Fan, Wood Plank and Herringbone Brick in eight designer colors.",
  };

  return (
    <div>
      <SEO
        title="Stamped Concrete Color & Pattern Studio | CG Concrete LLC"
        description="Explore stamped concrete patterns & colors from CG Concrete in Watertown, WI. Preview Ashlar Slate, Wood Plank, Herringbone & more across 8 designer colors. Free quote: (920) 253-5606."
        schema={schema}
      />

      {/* Hero */}
      <section className="relative bg-secondary text-white overflow-hidden">
        <div className="absolute inset-0 grain-texture opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary-foreground px-4 py-1.5 text-xs font-bold tracking-wider mb-6">
              <Palette className="h-3.5 w-3.5" /> STAMPED CONCRETE STUDIO
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.98]">
              Design Your Concrete <span className="text-primary">Color & Pattern</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl">
              Pick a color and watch it wrap across every stamp pattern we pour. It's the fastest way to picture your new patio, walkway, or pool deck before we break ground.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          {highlights.map((h) => (
            <div key={h.title} className="flex items-start gap-4 py-6 px-4">
              <span className="h-11 w-11 bg-primary/10 flex items-center justify-center shrink-0"><h.icon className="h-5 w-5 text-primary" /></span>
              <div>
                <p className="font-bold">{h.title}</p>
                <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{h.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The interactive studio */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-2xl mb-10">
          <span className="text-primary font-bold text-sm tracking-widest">THE STUDIO</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-2">Try Every Combination</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            {STAMP_PATTERNS.length} patterns × {STAMP_COLORS.length} colors. Select a color below to preview it live, then tap any pattern to view it full-size and request that exact look.
          </p>
        </div>
        <PatternStudioWidget color={studioColor} onColorChange={setStudioColor} />
      </section>

      {/* Book a free on-site consult */}
      <section className="bg-secondary text-white grain-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div className="lg:sticky lg:top-28">
            <span className="text-primary font-bold text-sm tracking-widest">BOOK A VISIT</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-2 leading-tight">Lock In a Free On-Site Consult</h2>
            <p className="text-white/80 mt-4 leading-relaxed text-lg">
              Found a look you love? Pick a date and we'll come measure, answer questions, and bring physical samples of your color — no pressure, no fee.
            </p>
            <ul className="mt-6 space-y-3 text-white/80">
              <li className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" /> On-site measurements &amp; honest, itemized pricing</li>
              <li className="flex items-start gap-3"><Palette className="h-5 w-5 text-primary shrink-0 mt-0.5" /> Real color &amp; pattern samples in hand</li>
              <li className="flex items-start gap-3"><Layers className="h-5 w-5 text-primary shrink-0 mt-0.5" /> Local crew that shows up when we say we will</li>
            </ul>
          </div>
          <ConsultScheduler selectedColor={studioColor} />
        </div>
      </section>

      {/* Color reference board */}
      <section className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-2xl mb-10">
            <span className="text-primary font-bold text-sm tracking-widest">THE PALETTE</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-2">The MasterColor® Palette</h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">The full {STAMP_COLORS.length}-color MasterColor lineup CG Concrete uses on stamped and colored concrete — grouped by family with product codes.</p>
          </div>
          <div className="space-y-10">
            {STAMP_COLOR_FAMILIES.map((fam) => (
              <div key={fam}>
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><span className="h-2 w-8 bg-primary" /> {fam}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {STAMP_COLORS.filter((c) => c.family === fam).map((c) => (
                    <div key={c.slug} className="group" data-testid={`palette-color-${c.slug}`}>
                      <div className="aspect-[4/3] ring-1 ring-black/10 transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-xl" style={{ backgroundColor: c.hex }} />
                      <div className="mt-2.5">
                        <p className="font-bold text-sm leading-tight">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.code}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="relative bg-secondary text-white overflow-hidden grain-texture p-10 sm:p-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold">Love a Combination?</h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto text-lg">Send us the pattern and color you picked and we'll build you a free, no-pressure estimate.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="h-13 px-8 py-6 bg-primary hover:bg-primary/90 rounded-sm font-bold text-base" data-testid="studio-cta-quote">
              <Link to="/contact?service=Stamped%20Concrete">Request Free Quote <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-13 px-8 py-6 rounded-sm font-bold text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary">
              <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-5 w-5" /> {COMPANY.phone}</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
